import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button, alert, Modal } from 'equasion-lib';
import { FiChevronLeft, FiMinusCircle, FiPlusCircle } from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import ZoneScanButton from '@/lib/shared-coms/ZoneScanner/ZoneScanButton';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import FadeLoader from 'react-spinners/FadeLoader';
import { useRootStore } from '@/lib/providers/RootProvider';
import { zoneSdSuffix, zoneQuarantineSuffix } from '@/lib/utils/zoneUtils';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { explainUnusableSerialScan } from '@/lib/scanning/explainScan';
import { playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { isShortDatedExpiration } from '@/lib/utils/shortDated';
import './SalesOrderPickScan.scss';

// AI-17 is a calendar date with no time; the server's MDate scalar runs input
// through new Date(v).toLocaleDateString() which shifts the date by a day when
// the server's tz is west of UTC. Sending noon UTC survives that round-trip in
// every real tz so the stored expiration matches what's on the barcode.
const barcodeExpirationToNoonUtcMs = (exp?: string): number | undefined => {
  if (!exp) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(exp);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], 12, 0, 0) : undefined;
};

function SalesOrderPickScan({
  salesOrder,
  orderItem,
  bulkInventoryEventCreate,
  refetch,
  zonesWithProduct = [],
  suggestedZoneId = null
}) {
  const selectedCompanyId = useRootStore((state) => state.selectedCompany.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanLot, setScanLot] = useState(null);
  const [mode, setMode] = useState('add');
  const [updateSubmitted, setUpdateSubmitted] = useState(false);
  const [zonesViewMoreOpen, setZonesViewMoreOpen] = useState(false);
  const [zonesPreviewOverflow, setZonesPreviewOverflow] = useState(false);
  const zonesPreviewRef = useRef(null);
  // Which zone the picker walked to, captured by scanning the rack label. A serialized pick
  // takes its zone from the item's own state (`useFromZone`), so this never changes what is
  // sent — it narrows the on-screen instruction from "these five zones" to "this one", and
  // the scan itself is refused if the zone holds none of this product.
  // Seeded with the stop the picking screen's walk sent the picker to, when that zone still
  // holds the product — so the instruction already reads "this one" on arrival. The picker
  // can still Change it; it is display-only either way (see above).
  const [pickingZone, setPickingZone] = useState<null | {
    id: string;
    name: string;
    zoneType?: string;
  }>(() => zonesWithProduct.find(({ id }) => id === suggestedZoneId) || null);
  const navigate = useNavigate();
  const { id: salesOrderId, vendorSalesOrderNumber } = salesOrder || {};
  const {
    quantity: salesOrderItemQuantity,
    serializedItemOfStatusCount,
    isShortDated: orderItemIsShortDated,
    isChargeback: orderItemIsChargeback,
    product: lineProduct = {}
  } = orderItem || {};
  const { id: productId, fdaProduct: { id: fdaProductId } = {} } = lineProduct;
  // const [currentPriorityLocationIndex, setCurrentPriorityLocationIndex] = useState(0);
  // const currentPriorityLocation = topPriorityLocations[currentPriorityLocationIndex];
  // const { name: priorityLocationName, priority } = currentPriorityLocation || {};
  const isAdd = mode === 'add';
  console.log('serializedItemOfStatusCount', serializedItemOfStatusCount);

  const returnToPicking = () => navigate(`/sales-order-picking/${salesOrderId}`);

  const zonesPreviewText = zonesWithProduct
    .map((zone) => zone.name + zoneQuarantineSuffix(zone) + zoneSdSuffix(zone))
    .join(', ');
  const zonesPreviewKey = zonesWithProduct.map(({ id }) => id).join(',');
  const handleZonesPreviewRef = (el) => {
    zonesPreviewRef.current = el;
    if (!el) return;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    setZonesPreviewOverflow((prev) => (prev === overflowing ? prev : overflowing));
  };

  // Client-side readback prompt: on manual entry we can't fully trust that the
  // typed serial/gtin is what's on the physical label, so we show one confirmation
  // modal before submitting. Scans bypass this — we trust the barcode. The state
  // holds the pending submission args until the user confirms or cancels.
  const [pendingManualConfirm, setPendingManualConfirm] = useState<null | {
    serialNumber: string;
    gtin: string;
    lot: string;
    expiration?: string;
    quantity: number;
    zoneId: string;
  }>(null);

  const submitBulkProductUpdate = async ({
    serialNumber,
    gtin,
    lot,
    expiration,
    quantity,
    zoneId,
    isScan = false
  }) => {
    // A barcode and a typed serial share this function. Only a scan gets a tone: the toast
    // from a typed entry is the operator looking at the screen on purpose.
    const say = (title, text, seconds, icon) =>
      isScan ? scanAlert(title, text, seconds, icon) : alert(title, text, seconds, icon);
    console.log('quantity', quantity);
    console.log('serializedItemOfStatusCount', serializedItemOfStatusCount);
    if (isAdd && serializedItemOfStatusCount >= salesOrderItemQuantity) {
      say(
        'All items have been picked',
        'You have picked all items for this order.',
        4,
        'warning'
      );
      return;
    }
    if (
      isAdd &&
      orderItemIsShortDated &&
      expiration &&
      !isShortDatedExpiration(barcodeExpirationToNoonUtcMs(expiration) ?? expiration)
    ) {
      say(
        'Short-dated lot required',
        'This order item requires short-dated inventory (expires within 180 days). The scanned lot does not qualify.',
        6,
        'error'
      );
      return;
    }
    try {
      setIsSubmitting(true);
      await bulkInventoryEventCreate({
        variables: {
          serialNumber,
          gtin,
          companyId: selectedCompanyId,
          useFromZone: !zoneId,
          inventoryEventAttributes: isAdd
            ? {
                productId,
                fdaProductId,
                salesOrderId,
                type: 'PICK',
                companyId: selectedCompanyId,
                zoneId
              }
            : {
                productId,
                fdaProductId,
                type: 'STOCK',
                companyId: selectedCompanyId,
                zoneId
              },
          limit: isAdd
            ? quantity || salesOrderItemQuantity - serializedItemOfStatusCount
            : serializedItemOfStatusCount,
          lot: lot ? lot.toUpperCase() : undefined,
          expiration: barcodeExpirationToNoonUtcMs(expiration)
        }
      });
      if (isAdd) {
        setUpdateSubmitted(true);
      }
      await refetch();
      if (isScan) playSuccessTone();
    } catch (e: any) {
      const errorMessage = e?.message || '';
      console.log('e.message', errorMessage);
      if (
        errorMessage.includes('Product unserialized') ||
        (errorMessage.includes('No Matching Products') && !!isScan && !!lot)
      ) {
        // The Apollo error link already raised the toast. The tone belongs with it; opening
        // the lot form is the recovery, not a successful pick.
        if (isScan) playFailTone();
        setScanLot(lot);
      } else if (
        errorMessage.includes(
          'Scanned item does not match the selected sales order item product'
        )
      ) {
        say(
          'Wrong product',
          'This GTIN and serial belong to a different product than this sales order line.',
          6,
          'error'
        );
      } else if (
        errorMessage.includes('Serialized item already picked for this sales order')
      ) {
        say(
          'Already picked',
          'This product is already picked for this sales order.',
          6,
          'warning'
        );
      } else if (
        errorMessage.includes("Cannot destructure property 'status'") ||
        errorMessage.includes('Serialized item state not found') ||
        (errorMessage.includes('No Matching Products') && !!isScan)
      ) {
        say(
          'Item not found',
          'The scanned GTIN and serial do not match a pickable item. Please verify and scan again.',
          6,
          'error'
        );
      } else if (errorMessage.includes('placeholder awaiting real-serial')) {
        say(
          'Unreconciled placeholder',
          'This item is an unserialized placeholder. Please scan the real GS1 barcode on the physical unit.',
          6,
          'warning'
        );
      } else if (errorMessage.includes('remaining to pick')) {
        if (errorMessage.includes('only 0 remaining to pick')) {
          say(
            'Already picked',
            'This sales order line is already fully picked.',
            6,
            'warning'
          );
        } else {
          say('Package Too Large', errorMessage, 6, 'error');
        }
      } else if (errorMessage.includes('chargeback')) {
        // `chargeback` and not `chargeback-eligible`: assertChargebackSerializedPick has two
        // wordings and only the vendor-less one carries the longer phrase (see
        // libs/services/src/order/chargeback-pick.ts). Lowercase is the discriminator — the
        // only other chargeback ValidationErrors in the services layer are the agreement
        // CRUD ones, which start "Chargeback agreement ..." and cannot reach a pick scan.
        // Ordered ahead of the short-dated branch because a line can be both, and being told
        // to find a short-dated lot is useless advice when the refusal was about provenance.
        say(
          'Chargeback stock required',
          operatorFacingMessage(
            e,
            'This order item must be filled from stock bought from the chargeback vendor. The scanned unit came from a different supplier.'
          ),
          6,
          'error'
        );
      } else if (errorMessage.includes('requires short-dated inventory')) {
        say(
          'Short-dated lot required',
          operatorFacingMessage(
            e,
            'This order item requires short-dated inventory. The scanned lot does not qualify.'
          ),
          6,
          'error'
        );
      } else {
        // Server-side rejections (wrong zone type, missing licence, closed-short PO,
        // …) already carry an operator-readable reason. Showing "please try again"
        // instead sends the picker back to rescan an item that will never scan.
        say(
          'Pick failed',
          operatorFacingMessage(e, 'Unable to process this scan. Please try again.'),
          6,
          'error'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSerializedPickScan = async (result) => {
    const serialNumber = result?.serialNumber?.trim();
    const gtin = result?.gtin?.trim();
    if (!serialNumber) {
      // Say why. A picker holding a handheld cannot tell "that barcode has no serial number"
      // from "the trigger did nothing" without being told.
      const refusal = explainUnusableSerialScan(result);
      if (refusal && !result?.isManualEntry) {
        scanAlert(refusal.title, refusal.message, 6, 'warning');
      }
      return;
    }

    // Manual entry: prompt the user to confirm what they typed before submitting.
    // Typos on a placeholder-reconciliation scan are hard to undo (the placeholder
    // gets stamped with the wrong real serial), so a one-click readback is worth
    // the extra step. Camera scans skip this — barcodes are trustworthy.
    if (result?.isManualEntry && isAdd) {
      setPendingManualConfirm({
        serialNumber,
        gtin,
        lot: result?.lot,
        expiration: result?.expiration,
        quantity: result?.quantity,
        zoneId: result?.zoneId
      });
      return;
    }

    await submitBulkProductUpdate({ ...result, serialNumber, gtin, isScan: true });
  };

  useEffect(() => {
    if (updateSubmitted && salesOrderItemQuantity === serializedItemOfStatusCount)
      returnToPicking();
  }, [serializedItemOfStatusCount]);

  return (
    <Page className="sales-order-pick-scan action-page">
      <div className="action-top-bar">
        <div className="top">
          <Button
            type="secondary"
            iconBefore={<FiChevronLeft />}
            onClick={returnToPicking}
          >
            Back
          </Button>
          {/* The SO number is the on-floor identity of what is being picked. It was always in this
              query and never rendered; with totes optional it is the substitute for the tote label
              a picker used to have in hand. Unconditional — useful either way. */}
          <div>
            Picking{vendorSalesOrderNumber ? ` — SO #${vendorSalesOrderNumber}` : ''}
          </div>
        </div>

        <div className="bottom">
          <div className="default-location">
            {pickingZone ? (
              <>
                Picking from:
                <div className="zones-preview zones-preview--active">
                  {pickingZone.name + zoneQuarantineSuffix(pickingZone)}
                </div>
                <Button
                  type="secondary"
                  className="zones-view-more-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPickingZone(null);
                  }}
                >
                  Change
                </Button>
              </>
            ) : (
              <>
                {zonesWithProduct.length === 1 ? 'Zone:' : 'Zones:'}
                <div
                  key={zonesPreviewKey}
                  ref={handleZonesPreviewRef}
                  className="zones-preview"
                >
                  {zonesPreviewText}
                </div>
                {zonesPreviewOverflow && (
                  <Button
                    type="secondary"
                    className="zones-view-more-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setZonesViewMoreOpen(true);
                    }}
                  >
                    View All
                  </Button>
                )}
                {!!zonesWithProduct.length && (
                  <ZoneScanButton
                    className="zones-scan-button"
                    companyId={selectedCompanyId}
                    title="Scan Pick Zone"
                    hint="Scan the rack label to confirm you are at the right zone for this product."
                    allowedZones={zonesWithProduct}
                    allowedZonesMessage="That zone holds no stock of this product for this order."
                    manualEntryLabel="Skip — pick without confirming the zone"
                    onSelect={setPickingZone}
                  />
                )}
              </>
            )}
          </div>
          <div className="sales-order-pick-scan__side-stack">
            <div className="sales-order-pick-scan__line-panel sales-order-pick-scan__line-panel--count">
              {serializedItemOfStatusCount} of {salesOrderItemQuantity}
            </div>
            <div className="sales-order-pick-scan__line-panel sales-order-pick-scan__line-panel--product">
              <WarehouseProductDisplay
                product={lineProduct}
                compact
                className="sales-order-pick-scan__product-display"
              >
                {orderItemIsShortDated && (
                  <span
                    style={{ color: 'var(--orange, orange)', fontWeight: 'bold' }}
                  >
                    SHORT DATED
                  </span>
                )}
                {/* Purple, distinct from the orange short-dated warning: this scan is refused
                    on where the units were BOUGHT, not on their expiration, and the two are
                    independent — a line can show both badges at once. */}
                {orderItemIsChargeback && (
                  <span
                    style={{ color: 'var(--purple, purple)', fontWeight: 'bold' }}
                  >
                    CHARGEBACK
                  </span>
                )}
              </WarehouseProductDisplay>
            </div>
          </div>
        </div>
      </div>
      <div className="action-page-body">
        <div
          className={['mode', mode].join(' ')}
          onClick={() => setMode(isAdd ? 'remove' : 'add')}
        >
          {mode} {isAdd ? <FiPlusCircle /> : <FiMinusCircle />}
        </div>
        <SharedScanner
          scanLot={scanLot}
          setScanLot={setScanLot}
          handleScan={handleSerializedPickScan}
          // alternativeManualEntry={
          //   <FulfillUnserializedForm
          //     scanLot={scanLot}
          //     // locations={topPriorityLocations}
          //     {...{
          //       isAdd,
          //       submitBulkProductUpdate: (result) => {
          //         const { lot, quantity, locationId } = result;
          //         if (isAdd) {
          //           navigate(
          //             `/sales-order-convert-to-serialized-pick-scan?salesOrderId=${salesOrderId}&salesOrderItemId=${salesOrderItem.id}&lot=${lot}&quantity=${quantity}&locationId=${locationId}`
          //           );
          //         } else {
          //           submitBulkProductUpdate({ ...result, isScan: false });
          //         }
          //       },
          //       totalQuantity: salesOrderItemQuantity,
          //       serializedItemOfStatusCount
          //     }}
          //   />
          // }
        />
        {!!isSubmitting && (
          <div
            className="blocking-spinner-container"
            onClick={(e) => e.stopPropagation()}
          >
            <FadeLoader color="var(--n0)" />
          </div>
        )}
      </div>
      <Modal
        className="zones-view-more-modal"
        modalOpen={zonesViewMoreOpen}
        setModalOpen={setZonesViewMoreOpen}
      >
        <div className="zones-view-more-list">
          <strong>Zones:</strong>
          {zonesWithProduct.map((zone) => (
            <div className="zones-view-more-row" key={zone.id}>
              <span>{zone.name + zoneQuarantineSuffix(zone)}</span>
              {zone.shortDatedQuantity > 0 && (
                <span className="zones-view-more-row__sd-badge">
                  SD {zone.shortDatedQuantity}
                </span>
              )}
            </div>
          ))}
        </div>
      </Modal>
      <Modal
        className="manual-confirm-modal"
        modalOpen={!!pendingManualConfirm}
        setModalOpen={(open) => {
          if (!open) setPendingManualConfirm(null);
        }}
      >
        {pendingManualConfirm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            <strong
              style={{
                fontSize: '1.8rem',
                lineHeight: 1.2
              }}
            >
              Confirm GTIN + Serial Number
            </strong>
            <p
              style={{
                fontSize: '1.4rem',
                color: 'var(--n100)',
                lineHeight: 1.4,
                padding: '0',
                margin: '0'
              }}
            >
              Double-check the values match what's on the physical unit before continuing
              —{' '}
              <strong style={{ color: 'yellow' }}>
                if the item is an unserialized placeholder, this pick will stamp it
                permanently with these values.
              </strong>
            </p>
            <div
              style={{
                padding: '1rem',
                background: 'var(--n700)',
                borderRadius: '0.8rem',
                fontSize: '1.5rem',
                display: 'flex',
                fontFamily: 'monospace',
                flexDirection: 'column',
                gap: '0.4rem',
                letterSpacing: '.08em'
              }}
            >
              <div>
                <span>GTIN:</span> {pendingManualConfirm.gtin || '(none)'}
              </div>
              <div>
                <span>Serial:</span> {pendingManualConfirm.serialNumber}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
              <Button
                type="secondary"
                onClick={() => setPendingManualConfirm(null)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={async () => {
                  const args = pendingManualConfirm;
                  setPendingManualConfirm(null);
                  // isScan:false so the mutation keeps its existing lot-passing
                  // behavior for manual entries.
                  await submitBulkProductUpdate({ ...args, isScan: false });
                }}
                disabled={isSubmitting}
              >
                Confirm &amp; pick
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Page>
  );
}

export default SalesOrderPickScan;
